import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, RefreshCw } from "lucide-react";
import { celebrate } from "@/components/Celebrate";

const LIKERT = [
  { v: 1, label: "Strongly disagree" },
  { v: 2, label: "Disagree" },
  { v: 3, label: "Neutral" },
  { v: 4, label: "Agree" },
  { v: 5, label: "Strongly agree" },
];

export default function Quiz() {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [labels, setLabels] = useState({});
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/quiz/questions"),
      api.get("/quiz/result").catch(() => ({ data: null })),
    ]).then(([q, r]) => {
      setQuestions(q.data.questions);
      setLabels(q.data.labels);
      if (r.data && r.data.scores) setResult(r.data);
    }).finally(() => setLoading(false));
  }, []);

  const answer = (v) => {
    const q = questions[idx];
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    if (idx < questions.length - 1) setIdx(idx + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(answers).map(([k,v]) => [String(k), v]));
      const res = await api.post("/quiz/submit", { answers: payload });
      setResult(res.data);
      toast.success("Quiz complete!");
      celebrate("Quiz complete! 🌅");
    } catch { toast.error("Submit failed"); }
    finally { setSubmitting(false); }
  };

  const restart = () => { setResult(null); setAnswers({}); setIdx(0); };

  if (loading) return <AppShell><div className="p-10 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading quiz…</div></AppShell>;

  // Results view
  if (result) {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto px-6 py-10" data-testid="quiz-result-page">
          <p className="text-[11px] uppercase tracking-[0.2em] text-mint-400" style={{ color: "#34D399" }}>Quiz complete</p>
          <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Your RIASEC profile</h1>
          <p className="text-slate-400 mt-2">Top codes: <b className="text-white">{result.top_codes.join(" · ")}</b></p>

          <div className="mt-8 brimi-card p-6">
            <div className="space-y-3">
              {Object.entries(result.scores).sort((a,b) => b[1]-a[1]).map(([code, val]) => (
                <div key={code}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-bold text-slate-900">{code} — {labels[code]}</span>
                    <span className="text-slate-500 font-semibold">{val}%</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${val}%`, background: colorFor(code) }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 mb-3">Suggested careers</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {result.suggested_careers.map((c) => (
                <div key={c.id} className="brimi-card p-4 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-500">via {c.match_code}</p>
                  </div>
                  <a href={`/careers?open=${c.id}`} data-testid={`result-career-${c.id}`} className="text-slate-500 hover:text-slate-900"><ArrowRight className="w-4 h-4" /></a>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="quiz-generate-roadmap" onClick={() => nav("/roadmap")} className="brimi-pill accent-coral flex items-center gap-2">
              Generate my roadmap <ArrowRight className="w-4 h-4" />
            </button>
            <button data-testid="quiz-restart" onClick={restart} className="brimi-pill bg-white/10 text-white flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Retake quiz
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  // Quiz taking view
  const q = questions[idx];
  const progress = ((idx) / questions.length) * 100;
  const selected = answers[q?.id];
  const allAnswered = Object.keys(answers).length === questions.length;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-6 py-10" data-testid="quiz-page">
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
            <span>Question {idx + 1} / {questions.length}</span>
            <span className="text-mint-400" style={{ color: "#34D399" }}>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${progress}%`, background: "#34D399" }} data-testid="quiz-progress-bar" />
          </div>
        </div>

        <div className="brimi-card p-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Interest Quiz · RIASEC</p>
          <h2 className="font-display text-2xl md:text-3xl text-slate-900 mt-3" data-testid="quiz-question">
            {q.text}
          </h2>

          <div className="mt-6 space-y-2">
            {LIKERT.map((opt) => (
              <button key={opt.v} data-testid={`quiz-option-${opt.v}`}
                onClick={() => answer(opt.v)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition font-semibold flex items-center justify-between ${
                  selected === opt.v ? "text-slate-900" : "border-slate-200 hover:bg-slate-50 text-slate-700"
                }`}
                style={selected === opt.v ? { background: "#FBBF24", borderColor: "#FBBF24" } : {}}>
                <span>{opt.label}</span>
                <span className="text-xs text-slate-500">{opt.v}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mt-6">
            <button data-testid="quiz-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="text-sm font-bold text-slate-500 hover:text-slate-800 disabled:opacity-40">← Previous</button>
            <button data-testid="quiz-next" disabled={idx >= questions.length - 1} onClick={() => setIdx(idx + 1)} className="text-sm font-bold text-slate-500 hover:text-slate-800 disabled:opacity-40">Next →</button>
          </div>
        </div>

        {allAnswered && (
          <div className="mt-6 flex justify-end">
            <button data-testid="quiz-submit-button" onClick={submit} disabled={submitting} className="brimi-pill accent-coral flex items-center gap-2 disabled:opacity-60">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              See my results
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function colorFor(c) {
  return { R:"#3B82F6", I:"#8B5CF6", A:"#FB7185", S:"#34D399", E:"#FBBF24", C:"#F97316" }[c];
}
